// src/utils/graphqlClient.js
import config from "../config";
import logger from "./logger";

/**
 * Fetches the list of organizations and their installations for the authenticated user.
 * Uses the user's Bearer access token for authentication via backend proxy.
 * Also performs cache validation and repair against DynamoDB cache.
 *
 * @param {Function} getAccessToken - Function that returns a valid access token (refreshes if needed)
 * @param {Function} onUnauthorized - Optional callback to handle 401 errors
 * @param {string} userId - Optional user ID for cache validation (email-based userId from DynamoDB)
 * @returns {Promise<Array>} Array of organizations with nested installations
 * @throws {Error} If the request fails
 */
export async function fetchOrganizationsWithInstallations(
  getAccessToken,
  onUnauthorized = null,
  userId = null
) {
  try {
    // Get fresh token (will refresh if expired)
    logger.log("[graphqlClient] Getting fresh access token...");
    const accessToken = await getAccessToken();

    const response = await fetch(
      `${config.servicesApiEndpoint}/organizations`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
          "x-api-key": config.apiKey,
        },
      }
    );

    if (response.status === 401) {
      logger.error("[graphqlClient] Received 401 Unauthorized");
      if (onUnauthorized) {
        onUnauthorized();
      }
      throw new Error("Unauthorized - session expired");
    }

    if (!response.ok) {
      const errorText = await response.text();
      logger.error(
        `Backend request failed with status ${response.status}:`,
        errorText
      );
      throw new Error(`Backend request failed with status ${response.status}`);
    }

    const result = await response.json();

    if (!result.organizations) {
      throw new Error("Invalid response format from backend");
    }

    logger.log("[graphqlClient] Successfully fetched organizations");
    logger.log(
      "[graphqlClient] Organization count:",
      result.organizations.length
    );
    logger.log(
      "[graphqlClient] Authorization status for all orgs:",
      result.organizations.map((o) => ({
        orgId: o.orgId,
        name: o.name,
        romanworksAuthzStatus: o.romanworksAuthzStatus,
      }))
    );
    logger.log(
      "[graphqlClient] Sample installation data:",
      result.organizations[0]?.installations?.[0] || "No installations"
    );

    // Cache validation removed - was causing issues by doing complete array replacement
    // instead of incremental updates. Cache updates now only happen via explicit
    // grant/revoke operations in the backend, and DynamoDB cache is verified
    // server-side in get-user-orgs.mjs using M2M token every 5 minutes.

    return result.organizations;
  } catch (error) {
    logger.error("Error fetching organizations:", error);
    throw error;
  }
}

/**
 * Caches organization/installation data to the backend.
 * This is called after user authenticates to ensure the sync Lambda has up-to-date org/install data.
 * The backend needs this data because M2M tokens don't have read:orgs scope.
 *
 * @param {Array} organizations - Array of organizations with nested installations
 * @returns {Promise<void>}
 */
export async function cacheOrganizationsToBackend(organizations) {
  try {
    const response = await fetch(
      `${config.servicesApiEndpoint}/cache-org-install`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": config.apiKey,
        },
        body: JSON.stringify({ organizations }),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      logger.warn(
        `Failed to cache org/install data (status ${response.status}): ${errorText}`
      );
      // Don't throw - this is non-critical, app can still function
      return;
    }

    const result = await response.json();
    logger.log("Successfully cached org/install data:", result);
  } catch (error) {
    // Log but don't throw - caching is non-critical
    logger.warn("Error caching org/install data to backend:", error);
  }
}
